import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ComboBox, Dropdown, TextInput } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { DatePicker } from 'components/date-picker';
import type { WellStatusRow } from 'components/well-cards';
import { getDateString } from 'utils/dates';
import {
  WELL_STATUS_LABELS,
  WELL_STATUS_ORDER,
  type WellStatusToken,
} from 'types/well-workspace';
import { DATE_INVALID_TEXT, parseTypedDate } from '../utils';
import styles from './inline-field.module.scss';

/** Any row this can edit — a Well Status row, or one of Well Details' identity fields. */
export type InlineRow =
  WellStatusRow | 'leaseName' | 'wellNumber' | 'currentOperator';

/* A frozen empty list for the `items` default: a fresh `[]` on every render is a new
   identity, which re-runs anything downstream that depends on it. */
const NO_ITEMS: string[] = [];

interface InlineFieldProps {
  row: InlineRow;
  label: string;
  /**
   * `select` for the status, `date` for a lifecycle date, `text` for a name, `combo` for
   * a value with suggestions but no fixed set — see the Current Operator field.
   */
  kind: 'select' | 'date' | 'text' | 'combo';
  /** `combo` only: what to suggest, and the typing to narrow it by. */
  items?: string[];
  onSearchChange?: (text: string) => void;
  placeholder?: string;
  /** The effective value — the server's, overlaid with any unsaved draft. ISO for dates. */
  value: string | null;
  /** What the row reads as when it is not being edited. */
  readOnly: React.ReactNode;
  /**
   * Commits the row. Called with `null` to clear. The editor closes on return — a value
   * this refuses is caught by `parseTypedDate` before it ever gets here.
   */
  onCommit: (row: InlineRow, value: string | null) => void;
  /**
   * Holds the editor open and shows this message — the form-level rule the LAST commit
   * broke, pointed at the row that has to change. Distinct from an unparseable date,
   * which is about the input rather than the form.
   */
  heldError?: string | null;
  /**
   * Whether this value diverges from the well's own record — staged on the server, or
   * committed here and not yet saved. Takes the edited wash, as the mock marks it.
   */
  dirty?: boolean;
  /** Nothing can be edited while the workspace is promoted, or mid-save. */
  disabled?: boolean;
}

/**
 * One Well-Status row, edited where it sits.
 *
 * Read mode is a real button — the pencil says the row is editable, and Enter/Space open
 * it without any key handling of our own. The pencil is ALWAYS drawn rather than revealed
 * on hover: a row that looks like static text until the mouse passes over it does not read
 * as editable, and hover reaches neither the keyboard nor touch. The Well Details card
 * takes the same position with `editAlwaysVisible`.
 *
 * COMMIT IS NOT `onBlur`. flatpickr renders its calendar into `<body>`, so clicking the
 * calendar icon or a day blurs the input and a blur-commit would tear the editor down
 * mid-interaction. Commit is instead: picking from the dropdown, Enter, a mousedown
 * outside the field (ignoring the portaled calendar), or a focusout that moved focus
 * somewhere real — that last one is what catches a reader who Tabs away, who fires
 * neither of the other two.
 * @param {InlineFieldProps} props The row, its value, and the commit.
 * @return {JSX.Element} The row's value — a button, or an open editor.
 */
// eslint-disable-next-line max-lines-per-function -- the body is one editor per `kind` — text, dropdown, combo, date — each a short branch; splitting them into four components would duplicate the open/close/commit machinery they share.
export function InlineField({
  row,
  label,
  kind,
  value,
  readOnly,
  onCommit,
  items = NO_ITEMS,
  onSearchChange,
  placeholder,
  heldError = null,
  dirty = false,
  disabled = false,
}: InlineFieldProps) {
  const [editing, setEditing] = useState(false);
  /** The raw text in a date input. flatpickr owns the field, so this mirrors it. */
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  /** Read by the document listener, which must not be re-bound on every keystroke. */
  const commitRef = useRef<() => void>(() => {});
  /**
   * Whether the reader TYPED, rather than picking from the calendar.
   *
   * This decides which value is authoritative, and it matters: flatpickr coerces rather
   * than refuses, so `13/45/2021` reaches its `onChange` as 2021-01-01 — a real date
   * nobody typed, which the server would accept without complaint. Once the field has
   * been typed into, the TEXT is the truth and `parseTypedDate` is the judge.
   */
  const typedRef = useRef(false);
  /** Guards the double-commit when Enter fires our handler and flatpickr's together. */
  const committedRef = useRef(false);
  /** Set when a commit closed the editor, so focus goes back after the re-render. */
  const refocusRef = useRef(false);
  /**
   * Whether the pending input event came from a KEYSTROKE.
   *
   * flatpickr rewrites the field to its own idea of the date after a failed parse, and
   * that rewrite arrives as an ordinary input event — which would otherwise clear the
   * "not a date" state the reader is supposed to see, and replace the text they typed
   * with the date flatpickr invented. Only a real keypress is allowed to do either.
   */
  const keyedRef = useRef(false);

  // A form-level refusal reopens the row it blames, so the thing to fix is where the
  // cursor already is rather than something the reader has to go and find.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a server refusal has to REOPEN the editor so the cursor lands where the error is; the error arrives from a mutation, not from this render.
    if (heldError) setEditing(true);
  }, [heldError]);

  /* An editing session starts whenever the editor OPENS, and the effect above opens it
     without going through `open`. The session state has to be reset here too or it
     survives from the last one — and `committedRef` surviving is fatal: `commitDate`
     returns on it, so the row reopened by a held error could never be committed again.
     That is a dead end, not a slow path: the reader types the very value the warning
     asked for, nothing saves, and the warning stands.

     Keyed on the OPEN TRANSITION, not on `editing` being true, so a re-render midway
     through an edit does not wipe what has been typed so far. */
  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (editing && !wasEditingRef.current) {
      committedRef.current = false;
      typedRef.current = false;
    }
    wasEditingRef.current = editing;
  }, [editing]);

  /** Leaves edit mode, discarding whatever is in the draft. */
  const close = () => {
    setEditing(false);
    setInvalid(false);
    // Deferred to an effect: the button is not mounted yet at this point, so focusing it
    // here would be a no-op and the focus ring would be lost to <body> on every commit.
    refocusRef.current = true;
  };

  /* Put the cursor in the field the click just opened. The shared DatePicker renders its
     own input and forwards no `autoFocus`, so without this the editor appears with focus
     left behind on a button that no longer exists — and the first thing typed goes to the
     page instead of the field. The dropdown opens its own menu via `initialIsOpen`. */
  useEffect(() => {
    if (!editing || kind === 'select' || kind === 'combo') return undefined;
    const timer = setTimeout(() => {
      const input = document.getElementById(`well-status-inline-${row}`);
      if (input instanceof HTMLInputElement) input.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [editing, kind, row]);

  /* Hand focus back to the row once it has rendered again — but NOT inside the key event
     that closed it. Committing with Enter closes the editor and renders the button in its
     place; focusing it synchronously puts it under the very same keypress, whose keyup
     then activates it and reopens the editor the reader just finished with. Yielding a
     task lets the keypress finish against nothing first. */
  useEffect(() => {
    if (editing || !refocusRef.current) return undefined;
    refocusRef.current = false;
    const timer = setTimeout(() => buttonRef.current?.focus(), 0);

    return () => clearTimeout(timer);
  }, [editing]);

  /** Enters edit mode, seeding the draft from the committed value. */
  const open = () => {
    if (disabled) return;
    setDraft(value ?? '');
    setInvalid(false);
    typedRef.current = false;
    committedRef.current = false;
    setEditing(true);
  };

  /** Commit a free-text row. Trimmed, and emptied to null rather than to "". */
  const commitText = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draft.trim();
    // Cleared rather than blanked: "" would store an empty string and read back as a
    // value the well does not have, which the promote gate would accept as a name.
    const next = trimmed === '' ? null : trimmed;
    if (next !== value) onCommit(row, next);
    close();
  };

  /** Commit the date editor's text. Holds the editor open when it does not parse. */
  const commitDate = () => {
    if (committedRef.current) return;
    /* Nothing typed means nothing to judge. `open` seeds the draft with the STORED value,
       which is ISO (`2026-10-10`) — `parseTypedDate` reads the MM/DD/YYYY a reader types,
       so handing it the seed fails every time and a reader who merely clicked into a
       filled date and back out was told it was malformed. A calendar pick does not come
       through here at all; it commits in the picker's own `onChange`. */
    if (!typedRef.current) {
      committedRef.current = true;
      close();

      return;
    }
    const parsed = parseTypedDate(draft);
    if (parsed === null) {
      setInvalid(true);

      return;
    }
    committedRef.current = true;
    // Unchanged is not an edit — committing it would arm a save that writes nothing.
    if ((parsed || null) !== value) onCommit(row, parsed || null);
    close();
  };
  const commitEditor: () => void = {
    date: commitDate,
    text: commitText,
    // A combo commits its TEXT, same as a plain one: the reader may have typed an
    // operator the catalog has never seen, and the suggestions are only suggestions.
    combo: commitText,
    // A dropdown has already committed by the time it closes.
    select: close,
  }[kind];
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: the outside-mousedown listener is bound once and must call the CURRENT committer, so assigning in an effect would commit a stale editor.
  commitRef.current = commitEditor;

  /* Closing on an outside MOUSEDOWN rather than a blur is the whole point: it can tell the
     page apart from the calendar flatpickr appended to <body>, which a blur cannot. The
     list-box guard is belt-and-braces — Carbon's Dropdown keeps its menu inline in this app
     — but it costs nothing and would matter the day `autoAlign` starts portaling. */
  useEffect(() => {
    if (!editing) return undefined;

    /**
     * Closes the editor on a mousedown outside it.
     * @param {MouseEvent} event The mousedown.
     */
    const onDown = (event: MouseEvent) => {
      // `instanceof Element`, not a null check: a mousedown dispatched at the document
      // itself has a target with no `closest`, and reading one would throw inside a
      // listener where nothing would catch it.
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (wrapRef.current?.contains(target)) return;
      if (target.closest('.flatpickr-calendar')) return;
      if (target.closest('.bx--list-box__menu')) return;
      commitRef.current();
    };

    document.addEventListener('mousedown', onDown);

    return () => document.removeEventListener('mousedown', onDown);
  }, [editing]);

  /* Tabbing out fires no mousedown and no Enter, so without this a keyboard reader's edit
     is silently never committed. Guarded on `relatedTarget`: a mouse click on a calendar
     day is not focusable and reports null, so this does NOT reintroduce the blur problem
     the mousedown listener exists to avoid. */
  /**
   * Commits and closes once focus leaves the editor entirely.
   * @param {React.FocusEvent<HTMLDivElement>} event The focusout.
   */
  const handleFocusOut = (event: React.FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as HTMLElement | null;
    if (!next) return;
    if (wrapRef.current?.contains(next)) return;
    if (next.closest('.flatpickr-calendar')) return;
    commitRef.current();
  };

  /* Only the input's OWN complaint is printed here. A form-level rule (`heldError`) is
     already named in full by the card's warning, and repeating the sentence beside the
     field said the same thing twice; what the row adds is being open and focused on the
     thing that has to change. A date that is not a date is different — nothing else
     reports that, because nothing else has seen the text. */
  const message = invalid ? DATE_INVALID_TEXT : null;

  if (!editing || disabled) {
    return (
      <button
        type="button"
        ref={buttonRef}
        className={classNames(styles.readButton, dirty && styles.dirty)}
        // The value is the button's text, so the name has to say what pressing it does.
        aria-label={`Edit ${label}`}
        disabled={disabled}
        onClick={open}
      >
        <span className={styles.readValue}>{readOnly}</span>
        <Edit size={16} className={styles.pencil} aria-hidden />
      </button>
    );
  }

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- not an interactive
       element: a wrapper that only listens so Escape and focus-out reach whichever editor is
       inside. The control itself is the input or dropdown it wraps, which carries the role. */
    <div
      ref={wrapRef}
      className={styles.editor}
      onBlur={handleFocusOut}
      /* On the wrapper rather than the input: the shared DatePicker spreads its rest props
         onto flatpickr's container, not the field, so it takes no `onKeyDown` — and key
         events bubble, so one handler here serves both editor kinds and lets Escape cancel
         the dropdown as well as the date. */
      onKeyDown={(event) => {
        if (event.key === 'Enter' && kind !== 'select') {
          event.preventDefault();
          commitEditor();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else {
          // Anything else is the reader editing the text, which is what licenses the
          // next input event to move the draft and clear the invalid state.
          keyedRef.current = true;
        }
      }}
    >
      {kind === 'text' && (
        /* A plain Carbon input, not `EditInPlace`: that component brings its own save and
           cancel buttons and its own chrome, which would make these three rows look and
           behave unlike every other editable row on the page. */
        <TextInput
          id={`well-status-inline-${row}`}
          size="sm"
          labelText={label}
          hideLabel
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
        />
      )}

      {kind === 'combo' && (
        /* A ComboBox, not a Dropdown: the catalog's operators are a SUGGESTION, not the
           permitted set — `current_operator` is free text on the well, and one built from
           a document can name an operator nothing in the index has seen. `allowCustomValue`
           is what keeps that possible; without it Carbon discards anything unmatched on
           close, which would silently drop the name the reader typed. */
        <ComboBox
          id={`well-status-inline-${row}`}
          size="sm"
          // ComboBox takes no `hideLabel` (Dropdown and TextInput do); the stylesheet
          // clips `.bx--label` for every editor here anyway, so it is hidden the same way.
          titleText={label}
          placeholder={placeholder ?? ''}
          items={items}
          initialSelectedItem={value ?? undefined}
          allowCustomValue
          // Open on mount, as the status dropdown does: one click from the pencil to the
          // list is the point of offering suggestions at all.
          downshiftProps={{ initialIsOpen: true }}
          shouldFilterItem={() => true}
          onInputChange={(next: string) => {
            setDraft(next ?? '');
            // The index is asked again as the reader narrows; filtering happens there
            // rather than locally, so `shouldFilterItem` passes everything through.
            onSearchChange?.(next ?? '');
          }}
          onChange={({ selectedItem }: { selectedItem?: string | null }) => {
            if (selectedItem === undefined) return;
            if (committedRef.current) return;
            committedRef.current = true;
            const next = (selectedItem ?? '').trim() || null;
            if (next !== value) onCommit(row, next);
            close();
          }}
        />
      )}

      {kind === 'select' && (
        /* Dropdown, not Select: Select renders a native <select>, whose options come up as
           the OS menu rather than a Carbon list box. Opened on mount so picking a value is
           still one click from the row. */
        <Dropdown
          id={`well-status-inline-${row}`}
          size="sm"
          titleText={label}
          hideLabel
          label="Select status"
          items={WELL_STATUS_ORDER}
          itemToString={(item: WellStatusToken | null) => {
            return item ? WELL_STATUS_LABELS[item] : '';
          }}
          selectedItem={(value as WellStatusToken | null) ?? null}
          invalid={Boolean(heldError)}
          invalidText={heldError ?? ''}
          downshiftProps={{ initialIsOpen: true }}
          onChange={({
            selectedItem,
          }: {
            selectedItem?: WellStatusToken | null;
          }) => {
            if (!selectedItem) return;
            if (selectedItem !== value) onCommit(row, selectedItem);
            close();
          }}
        />
      )}

      {kind === 'date' && (
        /* A picker AND a typed field: most of these dates come off a document, where
           typing the digits beats clicking back through a calendar.

           `onInputChange` is load-bearing, not a convenience — flatpickr only emits
           `onChange` on calendar-select or Enter, so a date that is typed and then clicked
           away from never reaches it and would be silently dropped. The shared DatePicker
           exposes this hook for exactly that. */
        <DatePicker
          id={`well-status-inline-${row}`}
          // The shared DatePicker has no `hideLabel`, and widening it for one caller is
          // not worth it — the `<dt>` beside this row already names the field, so the
          // label is hidden in the stylesheet and kept for assistive tech.
          labelText={label}
          size="sm"
          allowInput
          value={value ?? undefined}
          invalid={invalid || Boolean(heldError)}
          invalidText={invalid ? DATE_INVALID_TEXT : (heldError ?? '')}
          onChange={(dates: Date[]) => {
            // Typed text is judged by `parseTypedDate`, never by flatpickr — which would
            // happily hand back a date it invented from a typo.
            if (typedRef.current) {
              commitDate();

              return;
            }
            if (committedRef.current) return;
            committedRef.current = true;
            // An empty array is the reader clearing the field, which must send null rather
            // than leave the previous date standing.
            const next = dates[0] ? getDateString(dates[0]) : null;
            if (next !== value) onCommit(row, next);
            close();
          }}
          onInputChange={(event) => {
            if (!keyedRef.current) return;
            keyedRef.current = false;
            typedRef.current = true;
            setDraft(event.target.value);
            setInvalid(false);
          }}
        />
      )}

      {/* Our own message rather than the picker's `invalidText`.
          Carbon's DatePicker CLONES its input child and overwrites the child's `invalid`
          with its own (DatePicker.js:252-283), and `components/date-picker` passes
          `invalid` only to the input — so that prop never survives to render. Fixing the
          shared component would change every date field in the app; this row says its
          own piece instead. */}
      {message && (
        <p role="alert" className={styles.invalidText}>
          {message}
        </p>
      )}
    </div>
  );
}
